import * as Tone from "tone";
import { buildAudioGraph, type AudioGraph } from "./graph";
import type { FxParam } from "./fxBus";
import { decaySeconds, type EnvelopeParam } from "./envelope";
import type { DrumId } from "../constants/drums";
import type { KeyboardTrackId, TrackId } from "../constants/scale";
import { polyphonyVelocity } from "./velocity";

export type FxOrEnvelopeParam = FxParam | EnvelopeParam;

function isEnvelopeParam(param: FxOrEnvelopeParam): param is EnvelopeParam {
  return param === "decay";
}

export type TrackStepCallback = (step: number, time: number) => void;
export type TrackIntervals = Record<TrackId, string>;

const KEYBOARD_TRACKS: readonly KeyboardTrackId[] = ["keys", "bass", "lead"];
const ALL_TRACKS: readonly TrackId[] = ["drums", "keys", "bass", "lead"];

export interface AudioEngineApi {
  init(): Promise<void>;
  start(initialBpm?: number): Promise<void>;
  stop(): void;
  setBpm(bpm: number): void;
  setFx(track: TrackId, param: FxOrEnvelopeParam, value01: number): void;
  triggerDrum(id: DrumId, time?: number): void;
  setMelodyNoteAt(
    track: KeyboardTrackId,
    midi: number | null,
    time: number,
  ): void;
  setMelodyChordAt(
    track: KeyboardTrackId,
    midis: readonly number[],
    time: number,
  ): void;
  setTrackStepCallback(track: TrackId, cb: TrackStepCallback | null): void;
  setTrackInterval(track: TrackId, interval: string): void;
  setTrackIntervals(intervals: TrackIntervals): void;
  dispose(): void;
}

interface TrackSchedule {
  interval: string;
  callback: TrackStepCallback | null;
  stepCounter: number;
  repeatId: number | null;
}

class AudioEngine implements AudioEngineApi {
  private started = false;
  private initialized = false;
  private graph: AudioGraph | null = null;
  private heldMidis: Record<KeyboardTrackId, Set<number>> = {
    keys: new Set(),
    bass: new Set(),
    lead: new Set(),
  };
  private schedules: Record<TrackId, TrackSchedule> = {
    drums: { interval: "8n", callback: null, stepCounter: 0, repeatId: null },
    keys: { interval: "8n", callback: null, stepCounter: 0, repeatId: null },
    bass: { interval: "8n", callback: null, stepCounter: 0, repeatId: null },
    lead: { interval: "8n", callback: null, stepCounter: 0, repeatId: null },
  };

  async init() {
    if (this.initialized) return;
    await Tone.start();
    this.graph = buildAudioGraph();
    await this.graph.ready;
    this.initialized = true;
  }

  async start(initialBpm?: number) {
    await this.init();
    if (this.started) return;
    const transport = Tone.getTransport();
    if (initialBpm !== undefined) {
      transport.bpm.value = initialBpm;
    }
    for (const track of ALL_TRACKS) {
      this.armTrackSchedule(track, transport);
    }
    transport.start();
    this.started = true;
  }

  stop() {
    if (!this.started) return;
    const transport = Tone.getTransport();
    transport.stop();
    for (const track of ALL_TRACKS) {
      const sched = this.schedules[track];
      if (sched.repeatId !== null) {
        transport.clear(sched.repeatId);
        sched.repeatId = null;
      }
      sched.stepCounter = 0;
    }
    this.started = false;
    if (this.graph) {
      for (const track of KEYBOARD_TRACKS) {
        this.graph.synthVoices[track].releaseAllNow();
        this.heldMidis[track].clear();
      }
    }
  }

  setBpm(bpm: number) {
    const transport = Tone.getTransport();
    if (this.started) {
      transport.bpm.rampTo(bpm, 0.05);
    } else {
      transport.bpm.value = bpm;
    }
  }

  setFx(track: TrackId, param: FxOrEnvelopeParam, value01: number) {
    if (!this.graph) return;
    if (isEnvelopeParam(param)) {
      const seconds = decaySeconds(value01);
      if (track === "drums") {
        for (const voice of Object.values(this.graph.drumVoices)) {
          voice.setRelease(seconds);
        }
      } else {
        this.graph.synthVoices[track].setRelease(seconds);
      }
      return;
    }
    this.graph.buses[track].setParam(param, value01);
  }

  triggerDrum(id: DrumId, time?: number) {
    this.graph?.drumVoices[id].trigger(time);
  }

  setMelodyNoteAt(track: KeyboardTrackId, midi: number | null, time: number) {
    this.setMelodyChordAt(track, midi === null ? [] : [midi], time);
  }

  setMelodyChordAt(
    track: KeyboardTrackId,
    midis: readonly number[],
    time: number,
  ) {
    const voice = this.graph?.synthVoices[track];
    if (!voice) return;
    const held = this.heldMidis[track];
    const next = new Set(midis);
    for (const m of held) {
      if (!next.has(m)) voice.releaseAt(m, time);
    }
    const velocity = polyphonyVelocity(next.size);
    for (const m of next) {
      if (!held.has(m)) voice.attackAt(m, time, velocity);
    }
    this.heldMidis[track] = next;
  }

  setTrackStepCallback(track: TrackId, cb: TrackStepCallback | null) {
    this.schedules[track].callback = cb;
  }

  setTrackInterval(track: TrackId, interval: string) {
    const sched = this.schedules[track];
    if (sched.interval === interval) return;
    sched.interval = interval;
    if (!this.started) return;
    const transport = Tone.getTransport();
    if (sched.repeatId !== null) {
      transport.clear(sched.repeatId);
      sched.repeatId = null;
    }
    sched.stepCounter = 0;
    this.armTrackSchedule(track, transport);
  }

  setTrackIntervals(intervals: TrackIntervals) {
    for (const track of ALL_TRACKS) {
      this.setTrackInterval(track, intervals[track]);
    }
  }

  private armTrackSchedule(
    track: TrackId,
    transport: ReturnType<typeof Tone.getTransport>,
  ) {
    const sched = this.schedules[track];
    if (sched.repeatId !== null) {
      transport.clear(sched.repeatId);
      sched.repeatId = null;
    }
    sched.stepCounter = 0;
    sched.repeatId = transport.scheduleRepeat(
      (time) => {
        const step = sched.stepCounter;
        if (sched.callback) sched.callback(step, time);
        sched.stepCounter += 1;
      },
      sched.interval,
      0,
    );
  }

  dispose() {
    this.stop();
    this.graph?.dispose();
    this.graph = null;
    this.initialized = false;
  }
}

let engineInstance: AudioEngineApi | null = null;

export function getAudioEngine(): AudioEngineApi {
  if (!engineInstance) engineInstance = new AudioEngine();
  return engineInstance;
}

export function __resetAudioEngineForTests() {
  engineInstance?.dispose();
  engineInstance = null;
}
